import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Ship, Play, Anchor, Lock, WifiOff, Bell, DollarSign, KeyRound, Timer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useFleetStore } from '@/stores/fleet';
import { useEventsStore } from '@/stores/events';
import { computeMachineStatus, type MachineStatus } from '@/lib/machineStatus';
import { rentalsApi, type Rental } from '@/api/endpoints/rentals';

const STATUS_HEX: Record<MachineStatus, string> = {
  running: '#1FA463',
  ready: '#F59E0B',
  blocked: '#E02128',
  off: '#94A3B8',
};
const STATUS_LABEL: Record<MachineStatus, string> = {
  running: 'Navegando', ready: 'Atracada', blocked: 'Bloqueada', off: 'Offline',
};

function fmtBRL(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtH(ms: number) {
  return `${Math.round(ms / 3600000)}h`;
}
function monthKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function BIPage() {
  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);
  const unread = useEventsStore((s) => s.items.filter((e) => !e.read).length);
  const rentalsQ = useQuery({ queryKey: ['rentals'], queryFn: () => rentalsApi.list() });

  const fleet = useMemo(() => {
    const c: Record<MachineStatus, number> = { running: 0, ready: 0, blocked: 0, off: 0 };
    for (const d of devices.values()) c[computeMachineStatus(d, positions.get(d.id))] += 1;
    return c;
  }, [devices, positions]);
  const total = devices.size;

  const rental = useMemo(() => {
    const list: Rental[] = rentalsQ.data ?? [];
    const now = Date.now();
    const ativas = list.filter((r) => !r.finalizada).length;
    const mesStart = new Date(); mesStart.setDate(1); mesStart.setHours(0, 0, 0, 0);
    const noMes = list.filter((r) => r.saida_at && new Date(r.saida_at).getTime() >= mesStart.getTime());
    const receitaMes = noMes.reduce((s, r) => s + (r.valor ?? 0), 0);
    const horasMes = noMes.reduce((s, r) => {
      if (!r.saida_at) return s;
      const end = r.retorno_real_at ? new Date(r.retorno_real_at).getTime() : now;
      return s + Math.max(0, end - new Date(r.saida_at).getTime());
    }, 0);
    // Receita dos últimos 6 meses (barras)
    const buckets = new Map<string, number>();
    const ref = new Date(); ref.setDate(1); ref.setHours(0, 0, 0, 0);
    for (let i = 5; i >= 0; i--) {
      const d = new Date(ref); d.setMonth(d.getMonth() - i);
      buckets.set(monthKey(d), 0);
    }
    for (const r of list) {
      if (!r.saida_at || !r.valor) continue;
      const k = monthKey(new Date(r.saida_at));
      if (buckets.has(k)) buckets.set(k, (buckets.get(k) ?? 0) + r.valor);
    }
    const serie = Array.from(buckets.entries()).map(([k, v]) => ({
      mes: k.slice(5) + '/' + k.slice(2, 4), receita: Math.round(v),
    }));
    return { ativas, receitaMes, horasMes, serie };
  }, [rentalsQ.data]);

  const pie = (Object.keys(fleet) as MachineStatus[])
    .map((k) => ({ name: STATUS_LABEL[k], value: fleet[k], key: k }))
    .filter((d) => d.value > 0);

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-bold">Painel Executivo</h1>
        <p className="text-sm text-muted-foreground">Visão consolidada da frota, alertas e locadora.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi icon={Ship} label="Frota" value={String(total)} />
        <Kpi icon={Play} label="Navegando" value={String(fleet.running)} tone="text-status-running" />
        <Kpi icon={Anchor} label="Atracada" value={String(fleet.ready)} tone="text-status-ready" />
        <Kpi icon={Lock} label="Bloqueada" value={String(fleet.blocked)} tone="text-status-blocked" />
        <Kpi icon={WifiOff} label="Offline" value={String(fleet.off)} tone="text-status-off" />
        <Kpi icon={Bell} label="Alertas" value={String(unread)} tone="text-status-blocked" />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi icon={KeyRound} label="Locações ativas" value={String(rental.ativas)} />
        <Kpi icon={DollarSign} label="Receita do mês" value={fmtBRL(rental.receitaMes)} tone="text-status-running" />
        <Kpi icon={Timer} label="Horas locadas (mês)" value={fmtH(rental.horasMes)} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <p className="mb-3 text-sm font-bold">Distribuição da frota</p>
            {pie.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Sem embarcações.</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {pie.map((d) => <Cell key={d.key} fill={STATUS_HEX[d.key]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="mt-2 flex flex-wrap justify-center gap-3 text-xs">
              {pie.map((d) => (
                <span key={d.key} className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_HEX[d.key] }} />
                  {d.name} ({d.value})
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="mb-3 text-sm font-bold">Receita da locadora — últimos 6 meses</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={rental.serie}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis dataKey="mes" fontSize={11} />
                <YAxis fontSize={11} width={40} />
                <Tooltip formatter={(v: number) => fmtBRL(v)} />
                <Bar dataKey="receita" fill="#007AFF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }: { icon: typeof Ship; label: string; value: string; tone?: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
          <Icon className={`h-3.5 w-3.5 ${tone ?? ''}`} /> {label}
        </div>
        <p className={`mt-1 text-2xl font-bold leading-tight ${tone ?? ''}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
